<template>
  <div class="app-page__container app-page__container--no-sidebar">
    <div v-if="!isLoading">
      <h5 class="title is-5">
        {{ activeUserId === loggedInUser.id ? 'My Profile' : `${shapeUserDisplayName(activeUser)}'s Profile` }} <!-- eslint-disable-line max-len -->
      </h5>
      <div class="card">
        <div class="card-content">
          <form @submit.prevent="handleSubmitEditUserProfileForm">
            <div class="user-profile__avatar__container">
              <user-avatar :user="activeUser" size="large" />
            </div>
            <div class="field columns u-m-y-0">
              <b-field
                class="column u-p-y-0"
                :label="editUserProfileForm.firstName.label"
                :type="formErrors[editUserProfileForm.firstName.name] ? 'is-danger' : ''"
                :message="formErrors[editUserProfileForm.firstName.name] || ''"
              >
                <b-input
                  v-model="editUserProfileForm.firstName.value"
                  :disabled="isFormSubmissionInProgress || (!isLoggedInUserWorkspaceOwner && !isLoggedInUserActiveUser)"
                  @input="handleTouchForm"
                />
              </b-field>
              <b-field
                class="column u-p-y-0 u-m-b"
                :label="editUserProfileForm.lastName.label"
                :type="formErrors[editUserProfileForm.lastName.name] ? 'is-danger' : ''"
                :message="formErrors[editUserProfileForm.lastName.name] || ''"
              >
                <b-input
                  v-model="editUserProfileForm.lastName.value"
                  :disabled="isFormSubmissionInProgress || (!isLoggedInUserWorkspaceOwner && !isLoggedInUserActiveUser)"
                  @input="handleTouchForm"
                />
              </b-field>
            </div>
            <b-field
              :label="editUserProfileForm.email.label"
              :type="formErrors[editUserProfileForm.email.name] ? 'is-danger' : ''"
              :message="formErrors[editUserProfileForm.email.name] || ''"
            >
              <b-input
                v-model="editUserProfileForm.email.value"
                disabled
              />
            </b-field>
            <b-field :label="activeUserId === loggedInUser.id ? 'My Teams' : `Teams`">
              <div v-if="Object.keys(activeUserTeams).length > 0" class="card card-content">
                <div
                  v-for="team in Object.values(activeUserTeams)"
                  class="u-p-b-small"
                  :key="team.id"
                >
                  <span class="has-text-weight-bold">
                    {{ team.teamName }}
                  </span>
                  &nbsp;
                  <span class="is-size-7">
                    {{ team.userIds.length }}
                    {{ `Member${team.userIds.length > 1 ? 's' : ''}` }}
                  </span>
                </div>
              </div>
              <div v-else class="card card-content has-text-centered">
                <p class="has-text-grey-light">
                  {{ `${shapeUserDisplayName(activeUser)} has no teams.` }}
                </p>
              </div>
            </b-field>
            <b-field
              v-if="isLoggedInUserActiveUser"
              label="Appearance"
              class="u-m-t"
            >
              <div class="buttons has-addons">
                <b-button
                  v-for="option in themeOptions"
                  :key="option.value"
                  :type="themePreference === option.value ? 'is-primary' : ''"
                  @click="handleThemeChange(option.value)"
                >
                  <b-icon :icon="option.icon" size="is-small" />
                  &nbsp;{{ option.label }}
                </b-button>
              </div>
            </b-field>
            <b-field
              v-if="isLoggedInUserWorkspaceOwner || isLoggedInUserActiveUser"
              class="has-text-right u-p-t"
            >
              <b-button
                type="is-info"
                native-type="submit"
                :loading="isFormSubmissionInProgress"
              >
                Update Profile
              </b-button>
            </b-field>
          </form>
        </div>
      </div>
      <div v-if="isLoggedInUserWorkspaceOwner || isLoggedInUserActiveUser" class="card u-m-t">
        <div class="card-content">
          <p class="has-text-grey-light has-text-weight-bold">
            <b-icon icon="airplane" size="is-small" />&nbsp;Out of Office
          </p>
          <hr class="u-m-t-small">
          <div v-if="activeUser.oooRanges && activeUser.oooRanges.length > 0" class="u-m-b">
            <div
              v-for="(range, index) in activeUser.oooRanges"
              :key="index"
              class="ooo-range__row"
            >
              <span class="ooo-range__label">
                {{ range.startDate === range.endDate ? range.startDate : `${range.startDate} — ${range.endDate}` }}
              </span>
              <b-button
                type="is-text"
                size="is-small"
                class="has-text-danger"
                @click="handleRemoveOOORange(index)"
              >
                Remove
              </b-button>
            </div>
          </div>
          <p v-else class="has-text-grey u-m-b-small">No OOO periods scheduled.</p>
          <b-button
            type="is-info"
            outlined
            size="is-small"
            @click="isOOOModalOpen = true"
          >
            Add OOO Period
          </b-button>
        </div>
      </div>

      <b-modal :active.sync="isOOOModalOpen" has-modal-card trap-focus>
        <div class="modal-card">
          <header class="modal-card-head">
            <p class="modal-card-title">Add Out of Office Period</p>
          </header>
          <section class="modal-card-body">
            <b-field label="Start Date">
              <b-input v-model="oooForm.startDate" type="date" />
            </b-field>
            <b-field label="End Date">
              <b-input v-model="oooForm.endDate" type="date" />
            </b-field>
          </section>
          <footer class="modal-card-foot">
            <b-button type="is-info" @click="handleSaveOOORange">Save</b-button>
            <b-button @click="isOOOModalOpen = false">Cancel</b-button>
          </footer>
        </div>
      </b-modal>

      <div v-if="isLoggedInUserActiveUser" class="card u-m-t">
        <div class="card-content">
          <p class="has-text-grey-light has-text-weight-bold">
            <b-icon icon="robot" size="is-small" />&nbsp;Claude Integration
          </p>
          <hr class="u-m-t-small">
          <div v-if="mcpToken">
            <p class="u-m-b-small">
              <span class="has-text-grey">Token created</span>
              <span class="has-text-weight-bold u-m-l-small">{{ formatMcpTokenDate(mcpToken.createdAt) }}</span>
            </p>
            <b-field label="Connector URL">
              <b-input
                :value="mcpConnectorUrl"
                readonly
                expanded
                password-reveal
                type="password"
              />
              <p class="control">
                <b-button type="is-info" @click="handleCopyConnectorUrl">Copy</b-button>
              </p>
            </b-field>
            <p class="is-size-7 has-text-grey u-m-b">
              In Claude &rarr; Settings &rarr; Connectors &rarr; Add custom connector: paste the Connector URL above.
            </p>
            <b-button
              type="is-danger"
              outlined
              size="is-small"
              :loading="isMcpTokenLoading"
              @click="handleRevokeMcpToken"
            >
              Revoke Token
            </b-button>
          </div>
          <div v-else>
            <p class="has-text-grey u-m-b-small">
              Generate a token to connect StandupPro to Claude via the MCP server.
            </p>
            <b-button
              type="is-info"
              :loading="isMcpTokenLoading"
              @click="handleGenerateMcpToken"
            >
              Generate Token
            </b-button>
          </div>
        </div>
      </div>
      <b-button
        v-if="!isLoggedInUserActiveUser && isLoggedInUserWorkspaceOwner"
        type="is-text"
        size="is-small"
        class="user-profile__footer__archive-profile-button has-text-danger"
        outlined
        :disabled="isFormSubmissionInProgress"
        @click.prevent="handleToggleConfirmArchiveUserModal"
      >
        Archive User
      </b-button>
      <confirm-archive-user-modal
        :activeUserId="activeUserId"
        :isModalActive="isConfirmArchiveUserModalActive"
        @close="handleToggleConfirmArchiveUserModal"
        @userArchived="handleUserArchived"
      />
    </div>
    <div v-else style="position: relative; min-height: 150px;">
      <b-loading :is-full-page="false" :active.sync="isLoading" :can-cancel="false"></b-loading>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import * as UserActions from '@/store/actions/User.actions';
import * as TeamActions from '@/store/actions/Team.actions';
import * as ValidationService from '@/services/Validation.service';
import * as NotificationService from '@/services/Notification.service';
import * as notificationTypes from '@/constants/notificationTypes';
import * as DatabaseService from '@/services/Database.service';
import { isObjectEmpty } from '@/helpers/dataHelpers';
import { getUserTeams } from '@/helpers/teamHelpers';
import { confirmLeaveWithUnsavedChanges } from '@/helpers/formHelpers';
import { shapeUserDisplayName } from '@/helpers/userHelpers';
import UserAvatar from '@/components/UserAvatar.vue';
import ConfirmArchiveUserModal from '@/components/ConfirmArchiveUserModal.vue';

// Update this to the deployed MCP server base URL once available
const MCP_SERVER_BASE_URL = 'https://standuppro-production.up.railway.app';


export default {
  name: 'EditUserProfileView',

  components: {
    UserAvatar,
    ConfirmArchiveUserModal,
  },

  data() {
    return {
      activeUserId: null,
      isActiveUserLoading: true,
      areUserTeamsLoading: true,
      isConfirmArchiveUserModalActive: false,
      themeOptions: [
        { value: 'light', label: 'Light', icon: 'weather-sunny' },
        { value: 'system', label: 'System', icon: 'monitor' },
        { value: 'dark', label: 'Dark', icon: 'weather-night' },
      ],
      editUserProfileForm: {
        firstName: {
          name: 'First Name',
          label: 'First Name',
          value: '',
          required: true,
        },
        lastName: {
          name: 'Last Name',
          label: 'Last Name',
          value: '',
          required: true,
        },
        email: {
          name: 'Email',
          label: 'Email',
          value: '',
          required: true,
        },
      },
      isFormSubmissionInProgress: false,
      isFormTouched: false,
      formErrors: {},
      mcpToken: null,
      mcpTokenId: null,
      isMcpTokenLoading: false,
      isOOOModalOpen: false,
      oooForm: { startDate: '', endDate: '' },
    };
  },

  computed: {
    ...mapState({
      users: state => state.user.users,
      teams: state => state.team.teams,
    }),
    ...mapGetters([
      'activeWorkspace',
      'loggedInUser',
      'isLoggedInUserWorkspaceOwner',
    ]),
    isLoading() {
      return this.isActiveUserLoading || this.areUserTeamsLoading;
    },
    activeUser() {
      if (!this.activeUserId) return {};

      return this.users[this.activeUserId];
    },
    isActiveUserLoggedInUser() {
      if (!this.activeUserId || !this.loggedInUser.id) return false;

      return this.activeUserId === this.loggedInUser.id;
    },
    editUserProfileFormPayload() {
      return {
        userId: this.activeUserId,
        updatedUser: {
          firstName: this.editUserProfileForm.firstName.value,
          lastName: this.editUserProfileForm.lastName.value,
        },
        workspaceId: this.activeWorkspace.id,
      };
    },
    activeUserTeams() {
      return getUserTeams(this.activeUser.id, this.teams);
    },
    isLoggedInUserActiveUser() {
      return this.activeUserId === this.loggedInUser.id;
    },
    themePreference() {
      return this.loggedInUser.themePreference || 'system';
    },
    mcpConnectorUrl() {
      return `${MCP_SERVER_BASE_URL}/mcp?token=${this.mcpTokenId}`;
    },
  },

  created() {
    this.fetchActiveUserOnLoad();
  },

  beforeRouteLeave (to, from, next) { // eslint-disable-line
    confirmLeaveWithUnsavedChanges(this.isFormTouched, next);
  },

  watch: {
    '$route'() { // eslint-disable-line object-shorthand
      this.fetchActiveUserOnLoad();
    },
  },

  methods: {
    ...mapActions({
      fetchWorkspaceUsersByIds: UserActions.FETCH_WORKSPACE_USERS_BY_IDS,
      fetchUserTeams: TeamActions.FETCH_USER_TEAMS,
      fetchPublicUserTeams: TeamActions.FETCH_PUBLIC_USER_TEAMS,
      updateUserProfile: UserActions.UPDATE_USER_PROFILE,
      updateThemePreference: UserActions.UPDATE_THEME_PREFERENCE,
      updateOOORanges: UserActions.UPDATE_OOO_RANGES,
    }),
    setInitialFormState() {
      this.editUserProfileForm.firstName.value = this.activeUser.firstName;
      this.editUserProfileForm.lastName.value = this.activeUser.lastName;
      this.editUserProfileForm.email.value = this.activeUser.email;
    },
    handleSubmitEditUserProfileForm() {
      this.formErrors = ValidationService.runFormValidation({ ...this.editUserProfileForm });
      if (!isObjectEmpty(this.formErrors)) return;

      this.isFormSubmissionInProgress = true;
      this.updateUserProfile(this.editUserProfileFormPayload)
        .then(() => {
          this.isFormSubmissionInProgress = false;
          this.isFormTouched = false;
          NotificationService.showNotification(notificationTypes.UPDATE_USER_PROFILE_SUCCESS);
        })
        .catch(() => {
          this.isFormSubmissionInProgress = false;
          NotificationService.showNotification(notificationTypes.UPDATE_USER_PROFILE_ERROR);
        });
    },
    handleTouchForm() {
      this.isFormTouched = true;
    },
    handleToggleConfirmArchiveUserModal() {
      this.isConfirmArchiveUserModalActive = !this.isConfirmArchiveUserModalActive;
    },
    handleUserArchived() {
      this.isFormTouched = false;

      NotificationService.showNotification(notificationTypes.ARCHIVE_USER_SUCCESS);
      this.$router.push('/users');
    },
    async fetchActiveUserOnLoad() {
      if (this.$route.path === '/my-profile') {
        this.activeUserId = this.loggedInUser.id;
      } else {
        this.activeUserId = this.$route.params.id;
      }
      try {
        const userDataPromiseArray = [
          this.fetchWorkspaceUsersByIds({ userIds: [this.activeUserId], workspaceId: this.activeWorkspace.id }),
        ];

        if (this.isActiveUserLoggedInUser) {
          userDataPromiseArray.push(this.fetchUserTeams({
            userId: this.activeUserId,
            workspaceId: this.activeWorkspace.id,
          }));
        } else {
          userDataPromiseArray.push(this.fetchPublicUserTeams({ userId: this.activeUserId, workspaceId: this.activeWorkspace.id }));
        }

        await Promise.all(userDataPromiseArray);

        this.isActiveUserLoading = false;
        this.areUserTeamsLoading = false;

        this.setInitialFormState();

        if (this.isLoggedInUserActiveUser) {
          this.fetchMcpToken();
        }
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
      }
    },
    async fetchMcpToken() {
      const snapshot = await DatabaseService.getMcpTokenByUserId(this.loggedInUser.id);
      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        this.mcpTokenId = doc.id;
        this.mcpToken = doc.data();
      }
    },
    async handleGenerateMcpToken() {
      this.isMcpTokenLoading = true;
      try {
        const bytes = new Uint8Array(32);
        crypto.getRandomValues(bytes);
        const token = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');

        // Revoke any existing token first
        if (this.mcpTokenId) {
          await DatabaseService.deleteMcpToken(this.mcpTokenId);
        }

        await DatabaseService.setMcpToken(token, {
          workspaceId: this.activeWorkspace.id,
          userId: this.loggedInUser.id,
          email: this.loggedInUser.email,
        });

        this.mcpTokenId = token;
        this.mcpToken = { createdAt: new Date() };
      } catch (err) {
        NotificationService.showNotification(notificationTypes.GENERATE_MCP_TOKEN_ERROR);
      } finally {
        this.isMcpTokenLoading = false;
      }
    },
    async handleRevokeMcpToken() {
      this.isMcpTokenLoading = true;
      try {
        await DatabaseService.deleteMcpToken(this.mcpTokenId);
        this.mcpToken = null;
        this.mcpTokenId = null;
        NotificationService.showNotification(notificationTypes.REVOKE_MCP_TOKEN_SUCCESS);
      } catch (err) {
        NotificationService.showNotification(notificationTypes.REVOKE_MCP_TOKEN_ERROR);
      } finally {
        this.isMcpTokenLoading = false;
      }
    },
    handleCopyConnectorUrl() {
      navigator.clipboard.writeText(this.mcpConnectorUrl).then(() => {
        NotificationService.showNotification({ message: 'Connector URL copied to clipboard!', type: 'is-success' });
      });
    },
    formatMcpTokenDate(createdAt) {
      if (!createdAt) return '';
      const date = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
      return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    },
    handleThemeChange(value) {
      this.updateThemePreference({
        userId: this.loggedInUser.id,
        themePreference: value,
        workspaceId: this.activeWorkspace.id,
      });
    },
    handleSaveOOORange() {
      const { startDate, endDate } = this.oooForm;
      if (!startDate || !endDate) return;

      const effectiveEndDate = endDate < startDate ? startDate : endDate;
      const existingRanges = this.activeUser.oooRanges ? [...this.activeUser.oooRanges] : [];
      const updatedRanges = [...existingRanges, { startDate, endDate: effectiveEndDate }];

      this.updateOOORanges({
        userId: this.activeUserId,
        workspaceId: this.activeWorkspace.id,
        oooRanges: updatedRanges,
      }).catch(() => {
        NotificationService.showNotification(notificationTypes.UPDATE_USER_PROFILE_ERROR);
      });

      this.isOOOModalOpen = false;
      this.oooForm = { startDate: '', endDate: '' };
    },
    handleRemoveOOORange(index) {
      const updatedRanges = [...(this.activeUser.oooRanges || [])];
      updatedRanges.splice(index, 1);

      this.updateOOORanges({
        userId: this.activeUserId,
        workspaceId: this.activeWorkspace.id,
        oooRanges: updatedRanges,
      }).catch(() => {
        NotificationService.showNotification(notificationTypes.UPDATE_USER_PROFILE_ERROR);
      });
    },
    shapeUserDisplayName,
  },
};
</script>

<style scoped lang="scss">
.user-profile__avatar__container {
  display: flex;
  justify-content: center;
  margin-bottom: $global-whitespace-base;
}

.user-profile__footer__archive-profile-button {
  text-decoration: none;
  margin-top: $global-whitespace-small;
}

.ooo-range__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: $global-whitespace-xsmall 0;
  border-bottom: 1px solid $grey-lighter;
}
</style>
